import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './LoanCalculatorPage/components/CalculationForm';
import ResultsOverview from './LoanCalculatorPage/components/ResultsOverview';
import PaymentSchedule from './LoanCalculatorPage/components/PaymentSchedule';
import QuickAdjustmentSidebar from './LoanCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './LoanCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './LoanCalculatorPage/components/TipsSidebar';
import InfoContent from './LoanCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  loanAmount: '500000',
  loanTerm: '3', // Years
  interestRate: '12.5', // Annual percentage
  paymentType: 'annuity', // 'annuity' or 'differentiated'
  startDate: new Date().toISOString().split('T')[0],
  loanPurpose: 'consumer',
  includeEarlyRepayment: false,
  earlyRepaymentType: 'reduction', // 'reduction' (term) or 'payment' (monthly payment)
  earlyRepaymentAmount: '50000',
  earlyRepaymentPeriod: 'monthly', // 'monthly', 'quarterly', 'semi_annually', 'annually', 'once'
  earlyRepaymentStartMonth: '6', // Month number (1-based)
  includeFees: false,
  initialFee: '5000', // One-time initial fee
  monthlyFee: '0', // Monthly service fee or insurance
  monthlyFeeType: 'fixed', // 'fixed' or 'percent' (of remaining balance)
};

// Определение типов кредитов
export const loanTypes = [
  { id: 'consumer', name: 'Потребительский', defaultRate: 12.5 },
  { id: 'car', name: 'Автокредит', defaultRate: 9.0 },
  { id: 'refinance', name: 'Рефинансирование', defaultRate: 8.0 },
  { id: 'business', name: 'Бизнес-кредит', defaultRate: 14.0 },
  { id: 'education', name: 'Образовательный', defaultRate: 7.5 },
  { id: 'secured', name: 'Под залог имущества', defaultRate: 10.0 }
];

// Определение периодов досрочного погашения
export const earlyRepaymentPeriods = [
  { id: 'monthly', name: 'Ежемесячно' },
  { id: 'quarterly', name: 'Ежеквартально' },
  { id: 'semi_annually', name: 'Раз в полгода' },
  { id: 'annually', name: 'Ежегодно' },
  { id: 'once', name: 'Разово' }
];

/**
 * LoanCalculatorPage Component - Полный калькулятор кредита с различными типами платежей и дополнительными параметрами.
 */
const LoanCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('loan-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Кредитный калькулятор | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный кредитный калькулятор онлайн: расчет ежемесячного платежа (аннуитетный, дифференцированный), переплаты, построение графика платежей с учетом досрочного погашения, комиссий и различных видов кредитов.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedLoans, setSavedLoans] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных кредитов из localStorage ---
  useEffect(() => {
    const savedLoansData = localStorage.getItem('savedLoans');
    if (savedLoansData) {
      try {
        setSavedLoans(JSON.parse(savedLoansData));
      } catch (e) {
        console.error("Error loading saved loans:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const findRate = (id) => {
        const found = loanTypes.find(t => t.id === id);
        return found ? found.defaultRate.toString() : initialFormData.interestRate;
      };

      switch (subToolId) {
        case 'annuity':
          newState.paymentType = 'annuity';
          break;
        case 'differential':
          newState.paymentType = 'differentiated';
          break;
        case 'early-repayment':
          newState.includeEarlyRepayment = true;
          break;
        case 'car':
          newState.loanPurpose = 'car';
          newState.interestRate = findRate('car');
          break;
        case 'consumer':
          newState.loanPurpose = 'consumer';
          newState.interestRate = findRate('consumer');
          break;
        case 'business':
          newState.loanPurpose = 'business';
          newState.interestRate = findRate('business');
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Сохранение кредита в localStorage
  const saveLoan = (name) => {
    if (!results) return;

    const newSavedLoan = {
      id: Date.now().toString(),
      name: name || `Кредит ${formData.loanAmount}₽ на ${formData.loanTerm} лет`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      paymentType: formData.paymentType
    };

    const updatedSavedLoans = [...savedLoans, newSavedLoan];
    setSavedLoans(updatedSavedLoans);
    localStorage.setItem('savedLoans', JSON.stringify(updatedSavedLoans));

    return newSavedLoan;
  };

  // Удаление сохраненного кредита
  const deleteSavedLoan = (id) => {
    const updatedSavedLoans = savedLoans.filter(loan => loan.id !== id);
    setSavedLoans(updatedSavedLoans);
    localStorage.setItem('savedLoans', JSON.stringify(updatedSavedLoans));
  };

  // Загрузка сохраненного кредита
  const loadSavedLoan = (id) => {
    const loanToLoad = savedLoans.find(loan => loan.id === id);
    if (loanToLoad) {
      setFormData(loanToLoad.formData);
      setResults(loanToLoad.results);
      // Пересчитаем график платежей для визуализации
      // В реальном кейсе нужно будет либо хранить график в сохраненном кредите,
      // либо заново вызвать расчетную функцию
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Calculator className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Кредитный калькулятор'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              schedule={schedule}
              setSchedule={setSchedule}
              loanTypes={loanTypes}
              earlyRepaymentPeriods={earlyRepaymentPeriods}
              saveLoan={saveLoan}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <PaymentSchedule 
                  schedule={schedule} 
                  results={results}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент о кредитах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения кредитов --- */}
            <ComparisonSidebar 
              savedLoans={savedLoans}
              deleteSavedLoan={deleteSavedLoan}
              loadSavedLoan={loadSavedLoan}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Payment Schedule Table */
        .payment-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .payment-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default LoanCalculatorPage;